import { TraceMap, originalPositionFor, GREATEST_LOWER_BOUND, LEAST_UPPER_BOUND } from '@jridgewell/trace-mapping';

real.errorlogger = (function ($) {
    'use strict';

    // Cache of source map tracers keyed by .map file URL. Values are Promises
    // resolving to either a TraceMap or null (if the map could not be fetched/parsed).
    // The build guarantees that a bundle at <name>.<hash>.js has its sibling map at
    // <name>.<hash>.js.map (same hash), so the map URL can always be derived from
    // the JS URL by appending ".map".
    const sourceMapCache = {};

    const getSourceMapTracer = function (mapUrl) {
        if (!sourceMapCache[mapUrl]) {
            sourceMapCache[mapUrl] = fetch(mapUrl, { credentials: 'omit' })
                .then(response => response.ok ? response.json() : null)
                .then(rawMap => rawMap ? new TraceMap(rawMap) : null)
                .catch(() => null);
        }
        return sourceMapCache[mapUrl];
    };

    // Walk the stack and replace references to minified dist files
    // ("/dist/<file>.js:LINE:COL") with original source positions, by
    // fetching the matching .map file and consuming it on the client.
    const mapMinifiedSources = async function (dataPayload) {
        if (!dataPayload.error || !dataPayload.error.stack) {
            return;
        }

        const cdnUri = (real && real.cdnUri) ? real.cdnUri : '';
        if (!cdnUri) {
            return;
        }

        const escapedCdn = cdnUri.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(escapedCdn + '/dist/([\\w.-]+\\.js):(\\d+):(\\d+)', 'g');

        const lines = dataPayload.error.stack.split('\n');

        // Collect unique map files that we need to consume.
        const neededMaps = new Set();
        for (const line of lines) {
            pattern.lastIndex = 0;
            let m;
            while ((m = pattern.exec(line)) !== null) {
                neededMaps.add(m[1]);
            }
        }

        if (neededMaps.size === 0) {
            return;
        }

        const consumers = {};
        await Promise.all([...neededMaps].map(async file => {
            consumers[file] = await getSourceMapTracer(cdnUri + '/dist/' + file + '.map');
        }));

        for (let i = 0; i < lines.length; i++) {
            lines[i] = lines[i].replace(pattern, (match, file, lineStr, colStr) => {
                const tracer = consumers[file];
                if (!tracer) {
                    return match;
                }
                // @jridgewell/trace-mapping uses 1-based lines but 0-based columns (matching the
                // source map spec), while browser stack traces and IDEs use 1-based columns.
                // Convert the column on the way in and on the way out.
                const genLine = parseInt(lineStr, 10);
                const genCol = parseInt(colStr, 10) - 1;
                // Try the closest mapping <= the generated position first (default behavior).
                // If that misses (e.g. column falls before the first mapping on the line, or
                // between sparse mappings in minified output), fall back to the closest mapping
                // >= the generated position so we still get at least the source file and line.
                let pos = originalPositionFor(tracer, { line: genLine, column: genCol, bias: GREATEST_LOWER_BOUND });
                if (!pos || !pos.source) {
                    pos = originalPositionFor(tracer, { line: genLine, column: genCol, bias: LEAST_UPPER_BOUND });
                }
                if (!pos || !pos.source) {
                    return match;
                }
                // Source paths are typically relative to the dist file (e.g. "../frontend/js/foo.js").
                // Strip a leading ".." so the path can be resolved against the CDN root.
                const originalFile = cdnUri + pos.source.replace(/^\.\./, '');
                // Line/column should normally be present when source is set, but guard anyway.
                // The developer nearly always needs just the file + line; column is a nice-to-have.
                const origLine = pos.line ?? 1;
                const origCol = (pos.column ?? 0) + 1;
                return originalFile + ':' + origLine + ':' + origCol;
            });
        }

        dataPayload.error.stack = lines.join('\n');
    };

    // A case-insensitive list of known servers (starts with one of these strings).
    const knownServers = [
        'howspace', // Howspace PHP application.
        'awselb',   // AWS Load Balancer.
        'apache',   // Apache.
        'nginx'     // Nginx (used only in local stack).
    ];

    let loggerURL = '/api/jserrors';
    let loggedErrors = {};

    if (real.baseUri) {
        loggerURL = real.baseUri + loggerURL;
    }

    const knownErrorTypes = [
        'AjaxError',        // A custom error type generated only for AJAX errors thrown via real.ajax class.
        'Error',            // A general error.
        'EvalError',        // An error that occurs regarding the global function eval().
        'InternalError',    // An error that occurs when an internal error in the JavaScript engine is thrown. E.g. "too much recursion".
        'RangeError',       // An error that occurs when a numeric variable or parameter is outside of its valid range.
        'ReferenceError',   // An error that occurs when de-referencing an invalid reference.
        'SyntaxError',      // A syntax error that occurs while parsing code in eval().
        'TypeError',        // An error that occurs when a variable or parameter is not of a valid type.
        'URIError'          // An error that occurs when encodeURI() or decodeURI() are passed invalid parameters.
    ];

    const isKnownServer = function (server) {
        let isKnown = false;
        knownServers.forEach(function (knownServer) {
            if (server.toLowerCase().startsWith(knownServer)) {
                isKnown = true;
            }
        });
        return isKnown;
    };

    const isFalsePositive = function (error) {
        // Check script errors.
        if (error.message.match(/script error/i) && !error.lineNumber && !error.columnNumber) {
            // A script error happened, and we don't know the line or column number
            if (!error.url || error.url.match(/\/dist\/tier[1-3]\.min\.js/)) {
                // Either we don't know the URL or the error happened inside minified dist file
                // so it's impossible to debug the issue any further.
                return true;
            }
        }

        if (!error.stack) {
            // Error doesn't have a stack. Most likely the error happened in browser extension.
            // And anyhow we don't have any idea where the error happened so what can you do?
            return true;
        }

        // Message is just plain "NetworkError" without any additional information.
        if (error.message === 'NetworkError') {
            return true;
        }

        // Error happened in some blob: URL.
        if (error.url && error.url.substring(0, 5) === 'blob:') {
            return true;
        }

        // Error happened in some about:blank URL.
        if (error.url && error.url.substring(0, 11) === 'about:blank') {
            return true;
        }

        // Some browser plugin(s) can throw this error. We never use void(0) in our own code.
        if (error.message === '(void 0) is not a function') {
            return true;
        }

        // https://stackoverflow.com/questions/26483541/referenceerror-cant-find-variable-gcrweb#answer-27917744
        if (error.message.indexOf('__gCrWeb') > -1) {
            return true;
        }

        // Firefox for iOS injects window.__firefox__ helper scripts (reader mode, YouTube quality, ...)
        // that throw when their namespace isn't available yet. Same idea as the __gCrWeb rule above.
        if (error.message.indexOf('__firefox__') > -1) {
            return true;
        }

        // Crypto-wallet browser extensions (MetaMask, Coinbase, Phantom, ...) inject window.ethereum.
        // We never touch it; clashing extensions throw when redefining the provider.
        if (error.message.indexOf('window.ethereum') > -1
            || error.message.indexOf('Cannot redefine property: ethereum') > -1) {
            return true;
        }

        // The "Permission denied to access property" sometimes happens, because of some poorly done browser plugins.
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Property_access_denied
        if (error.message.indexOf('Permission denied to access property') > -1) {
            return true;
        }

        // "You can safely ignore this error. This error means that ResizeObserver was not able to deliver all
        // observations within a single animation frame. It is benign (your site will not break)."
        // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        if (error.message.indexOf('ResizeObserver loop limit exceeded') > -1) {
            return true;
        }

        const backendErrors = error.ajax?.response?.errors;
        if (Array.isArray(backendErrors) && backendErrors.length > 0) {
            const firstErrorCode = backendErrors[0]?.error?.code;
            if (firstErrorCode === 4002) {
                // Do not log "invalid_property_value" etc responses, they are shown and handled in UI already.
                return true;
            } else if (firstErrorCode === 409) {
                // Do not log "A page with path 'xx' already exist in the workspace 'xx" etc.
                return true;
            }
        }

        console.debug(error);

        return false;
    };

    const getErrorSeverity = function (dataPayload) {
        if (!dataPayload.error) {
            return 'error';
        }

        if (!dataPayload.error.url || dataPayload.error.url === '') {
            // We don't know where the error happened.
            return 'warning';
        }

        if (dataPayload.error.url) {
            // Check URL to see if error happened in some third party vendor library.
            if (dataPayload.error.url.match(/\/vendor\//) !== null) {
                // Error happened somewhere in /vendor/* path.
                return 'warning';
            }
        }

        if (dataPayload.error.message) {
            // Sometimes workers are using blob url but still keep "/vendor/" as part of the error message.
            if (dataPayload.error.message.match(/\/vendor\//) !== null) {
                // Error happened somewhere in /vendor/* path.
                return 'warning';
            }

            if (dataPayload.error.message.match(/script error/i)) {
                // Script errors are logged as warnings.
                return 'warning';
            }

            if (dataPayload.error.message.match(/loadHomepageTiles/i)) {
                // This is some iOS mumbo jumbo error that we don't care about.
                // https://stackoverflow.com/questions/51084241/what-is-cant-find-variable-loadhomepagetiles-error-on-ios-11-3
                return 'warning';
            }

            if (dataPayload.error.message.match(/NS_ERROR_/i)) {
                /**
                 * https://developer.mozilla.org/en-US/docs/Mozilla/Errors
                 *
                 * At least these have happened in production.
                 *
                 * NS_ERROR_UNEXPECTED (0x8000FFFF)
                 * An operation did not complete due to a condition which was not expected.
                 * This error occurred rather than continuing.
                 *
                 * NS_ERROR_NOT_CONNECTED (0x804B000C)
                 * The connection does not exist. XXX currently unused - consider removing.
                 */
                return 'warning';
            }

            if (dataPayload.error.message.match(/Use of const in strict mode/i)) {
                // The const keyword has been supported in strict mode for a long time.
                // This "error" can happen if user has a really old browser.
                return 'warning';
            }

            if (dataPayload.error.message.match(/Use of future reserved word in strict mode/i)) {
                // The const keyword has been supported in strict mode for a long time.
                // This "error" can happen if user has a really old browser.
                return 'warning';
            }

            if (dataPayload.error.message.match(/chrome-extension/i)) {
                // Treat Chrome extension errors as warnings
                return 'warning';
            }

            if (dataPayload.error.url === dataPayload.error.location) {
                // If the error url (in which file the error happened) and location (on which page
                // the error happened) are the same the error is most likely produced by
                // a browser plugin or something else to which we have zero control.
                return 'warning';
            }
        }

        // Check that the response came from a white listed location (Howspace PHP app or load balancer).
        if (dataPayload.error.type === 'AjaxError') {
            if (dataPayload.error.ajax) {
                if (dataPayload.error.ajax.response) {
                    if (dataPayload.error.ajax.response.headers) {
                        const server = dataPayload.error.ajax.response.headers['Server'] || '';
                        if (!isKnownServer(server)) {
                            // There is no server in response headers or the server is not known.
                            return 'warning';
                        }
                    }
                    if (dataPayload.error.ajax.response.status === 406) {
                        // This is a 406 AJAX error.
                        return 'warning';
                    }
                    if (dataPayload.error.ajax.response.status === 0) {
                        // This is a code zero (0) AJAX error. This can be considered as an "unknown error".
                        // https://stackoverflow.com/questions/872206/what-does-it-mean-when-an-http-request-returns-status-code-0#answer-14507670
                        return 'warning';
                    }
                }
            }
        }

        // This was old OVH related thing to avoid false positives. Testing to comment
        // out the whole block to better see AJAX errors in the errors list.
        /*if(dataPayload.error.type === 'AjaxError') {
            if(dataPayload.error.ajax) {
                if(dataPayload.error.ajax.response) {
                    if(dataPayload.error.ajax.response.headers) {
                        var hostname = dataPayload.error.ajax.response.headers['X-Apache-Hostname'] || '';
                        if(!hostname.match(/howspace/) && !hostname.match(/real4/)) {
                            // There is no hostname in response headers or it doesn't
                            // contain "real4" or "howspace" strings.
                            return 'warning';
                        }
                    }
                    if(dataPayload.error.ajax.response.status === 406) {
                        // This is a 406 AJAX error.
                        return 'warning';
                    }
                }
            }
        }*/

        // Everything else is logged as an error.
        return 'error';
    };

    const sendErrorToServer = function (errorMessage, url, lineNumber, columnNumber, error) {
        var dataPayload, ajaxMatch, arr, type, url, response;

        if (window.location.toString().substring(0, 7) === 'file://') {
            // Someone has saved this to their own computer. Do not send error logs anywhere.
            return;
        }

        if (real.window && real.window.isUnloading()) {
            // User is already navigating elsewhere, do not log the error.
            return;
        }

        if (errorMessage && errorMessage.match(/script error/i)) {
            // Unify all script errors to have the same message.
            errorMessage = 'Script error';
        }

        if (loggedErrors[encodeURIComponent(errorMessage)]) {
            // Same error was already logged.
            return;
        }

        // Add the error to logged errors.
        loggedErrors[encodeURIComponent(errorMessage)] = true;

        dataPayload = {
            client: {
                time: (new Date()).toString(),
                navigator: {
                    cookieEnabled: navigator.cookieEnabled,
                    userAgent: navigator.userAgent,
                    userAgentData: navigator.userAgentData ? navigator.userAgentData : null
                }
            },
            websocket: 'not-available',
            error: {
                type: 'UnknownError',
                message: errorMessage,
                lineNumber: lineNumber,
                columnNumber: columnNumber,
                url: url,
                location: window.location.toString()
            },
            real: {
                version: document.documentElement.dataset.howspaceVersion
            }
        };

        if (real.realtime && real.realtime.getActiveConnection()) {
            dataPayload.websocket = real.realtime.getActiveConnection().getStatus();
        }

        knownErrorTypes.forEach(function (type) {
            if (errorMessage.match(type)) {
                dataPayload.error.type = type;
            }
        });

        ajaxMatch = errorMessage.match(/AJAX (GET|POST|DELETE)(.)+/);
        if (ajaxMatch) {
            arr = ajaxMatch[0].split(' ');
            type = arr[1];
            url = arr[2];
            response = arr.slice(4).join(' ');

            dataPayload.error.message = type + ' ' + url;
            dataPayload.error.type = 'AjaxError';
            dataPayload.error.ajax = {
                type: type,
                url: url
            };

            try {
                const responseObj = JSON.parse(response);
                dataPayload.error.ajax.response = responseObj;
                if (responseObj?.responseJSON?.errors) {
                    dataPayload.error.ajax.response.errors = responseObj.responseJSON.errors;
                }
                if (error.responseHeaders) {
                    dataPayload.error.ajax.response.headers = error.responseHeaders;
                }
                if (error.dataPayload) {
                    dataPayload.error.ajax.dataPayload = error.dataPayload;
                }
                delete dataPayload.error.ajax.response.responseJSON;
                delete dataPayload.error.ajax.response.responseText;
            } catch (e) {
                dataPayload.error.ajax.response = response;
            }
        }

        if (error) {
            if (error.message && dataPayload.error.type !== 'AjaxError') {
                dataPayload.error.message = error.message;
            }
            if (error.stack) {
                dataPayload.error.stack = error.stack;
            }
        }

        if (isFalsePositive(dataPayload.error)) {
            // Do not log false positives.
            return;
        }

        dataPayload.error.severity = getErrorSeverity(dataPayload);

        mapMinifiedSources(dataPayload).finally(() => {
            $.ajax(loggerURL, {
                contentType: 'application/json',
                type: 'POST',
                data: JSON.stringify(dataPayload),
            });
        });
    };

    window.onerror = sendErrorToServer;

    return {

        sendErrorToServer: sendErrorToServer,

        cleanErrorStack: function (stack) {
            var stackArr = stack.toString().split('\n');

            if (stackArr[0].trim().toLowerCase() === 'error') {
                // In Chrome the stack starts with "Error", clean that away.
                stackArr.shift();
            }

            // Clean away the first entry from stack as that points to the
            // artificial error we have in the beginning of real.ajax method.
            stackArr.shift();

            stack = stackArr.join('\n');

            return stack;
        }

    };

})(jQuery);
